/**
 * Einen ABGEWORFENEN ORDNER auslesen.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „hier stimmt was niht. ich habe alle ordner mit allen dateien hochgeladen.
 *    nichts wird angezeigt."
 *
 * DAS IST DIE STELLE. Zieht man eine DATEI auf ein Abwurffeld, steht sie in
 * `dataTransfer.files`. Zieht man einen ORDNER darauf, steht dort ein Eintrag
 * ohne Inhalt - oder gar keiner. `Array.from(e.dataTransfer.files)` ergibt
 * dann eine leere Liste, es geht nichts hinaus, es kommt nichts an, und
 * niemand bekommt eine Meldung. Aus Sicht des Programms ist nichts passiert;
 * aus Sicht des Menschen davor hat er gerade dreitausend Bilder hochgeladen.
 *
 * DER ORDNER STEHT WOANDERS: in `dataTransfer.items`, über
 * `webkitGetAsEntry()`. Der Name trägt ein Herstellerkürzel und ist trotzdem
 * der einzige Weg, den alle Browser können - die genormte Nachfolgerin
 * (`getAsFileSystemHandle`) gibt es in Safari nicht.
 *
 * UND DER PFAD IST DAS EIGENTLICHE GUT. „01 Vorher/IMG_1.jpg" ohne den Pfad
 * ist nur noch „IMG_1.jpg", und dann liegt das Bild in der Wurzel statt im
 * Ordner. Jede Datei kommt deshalb MIT ihrem Pfad heraus.
 */

export interface Baumdatei {
  datei: File;
  /** Der Pfad samt Dateiname, mit Schrägstrichen, ohne führenden Schrägstrich. */
  pfad: string;
}

/** Wie tief ein Baum gelesen wird. Tiefer ist in der alten Ablage nichts. */
const HOECHSTE_TIEFE = 12;

/** Wie viele Dateien höchstens gelesen werden. Ein Schutz, keine Grenze. */
const HOECHSTENS = 20000;

function alsDatei(eintrag: any): Promise<File | null> {
  return new Promise((ja) => eintrag.file((f: File) => ja(f), () => ja(null)));
}

/**
 * Alle Einträge eines Verzeichnisses.
 *
 * IN SCHLEIFE, NICHT EINMAL. `readEntries` gibt höchstens hundert Einträge auf
 * einmal zurück und muss so lange erneut gefragt werden, bis eine leere Liste
 * kommt. Wer einmal fragt, verliert bei einem Ordner mit 250 Bildern die
 * letzten 150 - lautlos, und die Zahl sieht plausibel aus.
 */
function eintraege(leser: any): Promise<any[]> {
  return new Promise((ja) => {
    const alle: any[] = [];
    const weiter = () => leser.readEntries((teil: any[]) => {
      if (!teil.length) { ja(alle); return; }
      alle.push(...teil);
      weiter();
    }, () => ja(alle));
    weiter();
  });
}

async function geheDurch(eintrag: any, oben: string,
                         raus: Baumdatei[], tiefe: number): Promise<void> {
  if (raus.length >= HOECHSTENS || tiefe > HOECHSTE_TIEFE) return;
  const pfad = oben ? `${oben}/${eintrag.name}` : eintrag.name;
  if (eintrag.isFile) {
    const f = await alsDatei(eintrag);
    if (f) raus.push({ datei: f, pfad });
    return;
  }
  if (!eintrag.isDirectory) return;
  for (const kind of await eintraege(eintrag.createReader())) {
    await geheDurch(kind, pfad, raus, tiefe + 1);
  }
}

/**
 * Was auf dem Abwurffeld gelandet ist, als flache Liste mit Pfaden.
 *
 * `items` MUSS SOFORT GELESEN WERDEN. Die Liste ist nur während der
 * Abwurfbehandlung gültig; wer erst `await` sagt und danach zugreift, findet
 * eine leere. Deshalb werden die Einträge in der ersten Zeile eingesammelt
 * und erst danach durchgegangen.
 */
export async function ausAbwurf(dt: DataTransfer): Promise<Baumdatei[]> {
  const roh: any[] = [];
  for (const eintrag of Array.from(dt.items ?? [])) {
    const e = (eintrag as any).webkitGetAsEntry?.();
    if (e) roh.push(e);
  }

  if (!roh.length) {
    // Kein Browser, der Einträge hergibt: dann wenigstens die Dateien.
    return Array.from(dt.files ?? []).map((datei) => ({ datei, pfad: datei.name }));
  }

  const raus: Baumdatei[] = [];
  for (const e of roh) await geheDurch(e, '', raus, 0);
  return raus;
}

/**
 * Was ein `<input type="file" webkitdirectory>` hergibt.
 *
 * Hier bringt der Browser den Pfad selbst mit (`webkitRelativePath`). Fehlt
 * er - das kommt vor, wenn jemand über denselben Knopf einzelne Dateien wählt
 * - ist der Dateiname der Pfad. Nicht raten: dann liegt sie in der Wurzel,
 * und das ist die ehrliche Antwort.
 */
export function ausFeld(liste: FileList | null): Baumdatei[] {
  return Array.from(liste ?? []).map((datei) => ({
    datei,
    pfad: (datei as any).webkitRelativePath || datei.name,
  }));
}

/** Enthielt der Abwurf mindestens einen Ordner? */
export function hatOrdner(dt: DataTransfer): boolean {
  for (const eintrag of Array.from(dt.items ?? [])) {
    const e = (eintrag as any).webkitGetAsEntry?.();
    if (e?.isDirectory) return true;
  }
  return false;
}

/**
 * Der Baum, aufgeteilt in Pakete.
 *
 * EIN PAKET IST EINE SENDUNG. Zehntausend Dateien in einer wären eine Stunde
 * ohne ein Wort dazwischen, und ein Abbruch nach 55 Minuten kostet alles.
 * Aufgeteilt wird nach ANZAHL UND BYTES: dreissig Bilder von je acht Megabyte
 * sind kein kleines Paket.
 */
export function pakete(alles: readonly Baumdatei[],
                       hoechstensStueck = 60,
                       hoechstensBytes = 40 * 1024 * 1024): Baumdatei[][] {
  const raus: Baumdatei[][] = [];
  let jetzt: Baumdatei[] = [];
  let bytes = 0;
  for (const x of alles) {
    if (jetzt.length >= hoechstensStueck
        || (jetzt.length && bytes + x.datei.size > hoechstensBytes)) {
      raus.push(jetzt); jetzt = []; bytes = 0;
    }
    jetzt.push(x); bytes += x.datei.size;
  }
  if (jetzt.length) raus.push(jetzt);
  return raus;
}
